import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpStatusCode } from '@angular/common/http';

import { DashboardService } from '../../../../../service/events/dashboard.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';

import { BaseForm } from '../../../../base-form';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { environment } from '../../../../../../environments/environment';
import {
	ReactiveDropdownSyncController,
	ReactiveSelectionResolver,
	type ReactiveSelectionResolverConfiguration,
} from '@fitogest/reactive-form-builder/utils';

import { FincasEnvironmentDefinition } from '../environment-variables';
import { FincasFormDefinition } from './form-definition';
import { ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import { ReactiveFormStableValueChange, FormType } from '@fitogest/reactive-form-builder/interfaces';

import { FincasModel } from '../../../../../models/form-common/fincas.module';
import { BaseResponseModel } from 'src/app/models/base-response.model';
import type { ReaImportData, ReaModalModel } from 'src/app/models/fincas/rea.model';
import { MessageService } from 'primeng/api';
import { Utils } from 'src/app/common/utils';

const FINCA_NAME = environment.features.fincaName;

const REA_IMPORTED_FINCA_ID_CONFIGURATION: ReactiveSelectionResolverConfiguration = {
	idKeys: ['idFinca', 'fincaId', 'id'],
};

const REA_CLIENT_ID_CONFIGURATION: ReactiveSelectionResolverConfiguration = {
	idKeys: ['id', 'id_cliente'],
};


@Component({
	selector: 'app-finca-form',
	standalone: false,
	templateUrl: './finca-form.component.html',
	styleUrls: ['./finca-form.component.scss'],
})
export class FincaFormComponent extends BaseForm<FincasModel> implements OnInit {
	/***************************
	 * Form Builder Variables **
	 ***************************/
	public form!: FincasFormDefinition;

	public readonly formRequest: ReactiveFormRequestCoordinator<FincasModel>;

	public reactiveFormRecordId = '';
	/***************************************************************************** */

	public showReaImportDialog = false;

	public readonly env: FincasEnvironmentDefinition = new FincasEnvironmentDefinition();

	private pendingReaImportedFincaId: number | null = null;

	/**
	 * Initializes the finca form container component, configures the inherited
	 * CRUD form workflow, and creates the reactive form request engine used by
	 * the builder layer to execute phase-based form actions.
	 *
	 * The injected services are intentionally split by responsibility:
	 * - routing services resolve the current navigation context and allow redirection,
	 * - dashboard services integrate the component into the parent form lifecycle,
	 * - API services provide entity persistence and dropdown data sources,
	 *
	 * @param router Router instance used to navigate after successful form actions
	 * and to transition between create, edit, and duplication flows.
	 * @param route Activated route that provides route parameters and contextual data
	 * required to resolve the current form request type and target entity identifier.
	 * @param dashboard Dashboard service consumed by the base form component to
	 * integrate this form into the shared page and navigation workflow.
	 * @param fincasApi API service that exposes finca CRUD endpoints and is used as
	 * the main persistence gateway for the current entity.
	 * @param clientesApi API service used to resolve and populate client-related
	 * sources required by the finca form configuration.
	 * @param productosApi API service used to resolve and populate product-related
	 * sources required by dependent form controls and auxiliary data flows.
	 */
	constructor(
		public override router: Router,
		public override route: ActivatedRoute,
		public override dashboard: DashboardService,
		private readonly messageService: MessageService,
		public fincasApi: FincasApiService,
		public clientesApi: ClientesApiService,
		public productosApi: ProductosApiService,
	) {
		super(
			route,
			router,
			dashboard,
			fincasApi.fincasPUT,
			fincasApi.fincasPOST,
			'finca',
			`Editar ${FINCA_NAME}`,
			`Crear ${FINCA_NAME}`,
			`Duplicar ${FINCA_NAME}`,
		);
		this.formRequest = new ReactiveFormRequestCoordinator<FincasModel>();
	} // ();

	/**
	 * Initializes the finca form component.
	 *
	 * This lifecycle hook creates the finca form definition, registers the REA import
	 * callback, starts the dropdown data initialization and applies the default
	 * client when REA mode is disabled.
	 *
	 * @public
	 * @override
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The dropdown initialization promise is explicitly ignored with the `void`
	 * operator because the method does not need to await the result during component
	 * initialization.
	 *
	 * The `openReaImport` action is passed to the form definition so header buttons
	 * or child form controls can trigger the REA import dialog through the component.
	 *
	 * @example
	 * ```ts
	 * this.ngOnInit();
	 * ```
	 *
	 * @see FincasFormDefinition
	 * @see getType
	 * @see initializeDropdownData
	 * @see openReaImportDialog
	 * @see setDefaultClientWhenReaIsInactive
	 */
	public ngOnInit(): void {
		this.resetExternalDropdowns();
		this.softInit(this.getType());
		const routeModel = this.getModel() as unknown as FincasModel;

		this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
		this.initializeHostModel(routeModel);
		this.form = new FincasFormDefinition(
			this.getType(),
			null,
			{
				openReaImport: (): void => {
					this.openReaImportDialog();
				},
			},
			{
				clientes: this.clientesApi.clientesGET,
				productos: this.productosApi.productos.GET,
			},
		);

		void this.initializeDropdownData();

		this.setDefaultClientWhenReaIsInactive();
	}// ();

	/**
	 * Opens the REA import dialog when the required finca data is available.
	 *
	 * This method validates the current form model before showing the REA import
	 * dialog. If the required information is missing, the dialog remains closed.
	 *
	 * @private
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The validation is delegated to `hasRequiredInfoFilled`, which also displays the
	 * corresponding user-facing error messages.
	 *
	 * @example
	 * ```ts
	 * this.openReaImportDialog();
	 * ```
	 *
	 * @see hasRequiredInfoFilled
	 */
	private openReaImportDialog(): void {
		if (!this.hasRequiredInfoFilled) {
			return;
		}// if();

		this.showReaImportDialog = true;
	}// ();

	/**
	 * Assigns the current user as the default client when REA mode is disabled.
	 *
	 * This method avoids modifying the client field when REA mode is active. When REA
	 * mode is inactive, it reads the current user identifier, validates that it can
	 * be converted to a number and assigns it to the form model as `id_cliente`.
	 *
	 * @private
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * If the current user identifier cannot be converted into a valid number, the
	 * form model is not updated and an error message is displayed to the user.
	 *
	 * @example
	 * ```ts
	 * this.setDefaultClientWhenReaIsInactive();
	 * ```
	 *
	 * @see StorageManager.getUser
	 * @see MessageService.add
	 */
	private setDefaultClientWhenReaIsInactive(): void {
		if (this.env.activateREA) {
			return;
		}

		const loggedInClient = StorageManager.getUser();
		const clientId = Number(loggedInClient.id);

		if (Number.isNaN(clientId)) {
			this.messageService.add({
				severity: 'error',
				summary: '',
				detail: 'No se ha podido obtener el cliente del usuario actual.',
			});

			return;
		}

		this.form.model.id_cliente = clientId;
	}// ();

	/**
	 * Updates the internal form state with the latest model snapshot emitted by the
	 * reactive form builder and stores the current form tag associated with that change.
	 *
	 * This method is typically invoked from the builder change event and keeps the
	 * local form definition synchronized with the current UI state.
	 *
	 * @param formModel Latest form model emitted by the reactive form builder.
	 * @param tag Identifier associated with the emitted form state or current form context.
	 * @returns void
	 */
	public stableValueChanges(event: ReactiveFormStableValueChange<FincasModel>): void {
		const stableValue = this.captureStableValueChange(event.value as FincasModel);

		this.form.model = stableValue as typeof this.form.model;
		this.form.lastChangedFieldName = event.changedField;
		this.syncModelFromForm();
	} // ();

	/**
	 * Finalizes the host-owned dropdown lifecycle for catalogs now loaded declaratively.
	 *
	 * The field definitions own the actual data-source requests. The host still emits the
	 * external-dropdown completion signal so previous-data mapping and builder readiness
	 * keep the same lifecycle boundary used by the rest of the CRUD forms.
	 *
	 * @returns A promise that resolves to `true` after the finalization trigger has been scheduled.
	 */
	private syncModelFromForm(): void {
		const normalizedModel = { ...(this.form.model as Record<string, unknown>) } as unknown as FincasModel;

		this.form.model = normalizedModel as typeof this.form.model;
		this.updateHostModel(normalizedModel);
	} // ();

	private initializeDropdownData(): Promise<boolean> {
		queueMicrotask(() => {
			this.finalizeExternalDropdowns();
			this.refreshReactiveDropdownMappings();
		});

		return Promise.resolve(true);
	}// ();

	private refreshReactiveDropdownMappings(): void {
		ReactiveDropdownSyncController.refreshMappings({ formBuilder: this.formBuilderRef });
	} // ();
	/***************************************************************************** */

	/******************
	 * REA Functions **
	 *****************/
	/**
	 * Handles the result returned by the REA import workflow.
	 *
	 * When the import completes successfully with any 2xx status, this method
	 * triggers a finca data refresh so the UI can reflect the imported state.
	 *
	 * @param result Response wrapper returned by the REA import operation.
	 * @returns void
	 */
	public onReaImported(result: BaseResponseModel<ReaImportData>): void {
		if (!this.isSuccessfulReaResponse(result.statusCode)) {
			return;
		} // if();

		this.fincasApi.fincasGET.safePerform();

		if (this.getType() !== FormType.CREATION) {
			return;
		} // if();

		const importedFincaId = this.extractImportedFincaId(result);

		if (importedFincaId === null) {
			return;
		} // if();

		this.pendingReaImportedFincaId = importedFincaId;
	} // ();

	/**
	 * Handles REA dialog visibility changes.
	 *
	 * When REA import creates a finca from the create form, the user is redirected
	 * to the edit view after closing the summary modal so the imported official data
	 * is immediately visible in the form.
	 *
	 * @param isVisible Current dialog visibility state.
	 * @returns void
	 */
	public onReaDialogVisibilityChange(isVisible: boolean): void {
		this.showReaImportDialog = isVisible;

		if (isVisible) {
			return;
		} // if();

		this.navigateToImportedFincaIfNeeded();
	} // ();

	/**
	 * Checks whether the REA response completed successfully.
	 *
	 * @param statusCode HTTP status code returned by the backend wrapper.
	 * @returns True when the status code belongs to the 2xx range.
	 */
	private isSuccessfulReaResponse(statusCode: number): boolean {
		return statusCode >= HttpStatusCode.Ok && statusCode < HttpStatusCode.MultipleChoices;
	} // ();

	/**
	 * Extracts the finca identifier returned by the REA import backend.
	 *
	 * @param result Response wrapper returned by the REA import operation.
	 * @returns Imported finca identifier or null when it is not available.
	 */
	private extractImportedFincaId(result: BaseResponseModel<ReaImportData>): number | null {
		return ReactiveSelectionResolver.toPositiveIntegerId(
			result.firstOrNull(),
			REA_IMPORTED_FINCA_ID_CONFIGURATION,
		);
	} // ();

	/**
	 * Redirects from create mode to the edit form of the finca created by REA import.
	 *
	 * @returns void
	 */
	private navigateToImportedFincaIfNeeded(): void {
		const fincaId = this.pendingReaImportedFincaId;

		if (fincaId === null) {
			return;
		} // if();

		this.pendingReaImportedFincaId = null;
		void this.router.navigate(['dashboard', 'editar-finca', fincaId]);
	} // ();

	/**
	 * Resolves the current client identifier without coercing invalid values to NaN.
	 *
	 * @returns Numeric client id when available; otherwise, undefined.
	 */
	private getClientIdForReaImport(): number | undefined {
		return ReactiveSelectionResolver.toPositiveIntegerId(
			this.form.model.id_cliente,
			REA_CLIENT_ID_CONFIGURATION,
		) ?? undefined;
	}

	/**
	 * Builds the input model required by the REA import modal from the current
	 * finca form state.
	 *
	 * This method extracts the REA code, holder tax identifier, finca name, and
	 * client identifier from the current form model, normalizing optional values
	 * to `null` when they are not available.
	 *
	 * @returns REA modal payload generated from the current finca form model.
	 */
	public buildReaModalModel(): ReaModalModel {
		const nif = this.form.model.nif ?? this.form.model.titular_nif;

		return {
			codigo_REA: this.form.model.codigo_REA,
			titular_nif: nif ?? '',
			nombre_finca: this.form.model.nombre ?? '',
			id_cliente: this.getClientIdForReaImport(),
		};
	} // ();

	/**
	 * Indicates whether the finca has the minimum required data to import REA data.
	 *
	 * This getter validates the current form model before starting the REA import
	 * process. It checks that a valid NIF is available, using the finca NIF first and
	 * falling back to the titular NIF when needed, and also checks that the finca
	 * name has been provided.
	 *
	 * @private
	 *
	 * @returns `true` when all required REA import data is filled; otherwise, `false`.
	 *
	 * @remarks
	 * Each validation error is collected first and then displayed through the
	 * message service, allowing the user to see all missing requirements at once.
	 *
	 * The NIF validation uses `Utils.hasValue` to avoid accepting empty, null or
	 * undefined values.
	 *
	 * @example
	 * ```ts
	 * if (!this.hasRequiredInfoFilled) {
	 *   return;
	 * }
	 * ```
	 *
	 * @see FincasModel
	 * @see Utils.hasValue
	 * @see MessageService.add
	 */
	private get hasRequiredInfoFilled(): boolean {
		const model = this.form.model;
		const nif = Utils.hasValue(model.nif) ? model.nif : model.titular_nif;
		const errorDetails: string[] = [];

		if (!Utils.hasValue(nif)) {
			errorDetails.push('NIF requerido para la importación del REA.');
		}// if();

		if (!Utils.hasValue(model.nombre)) {
			errorDetails.push('Nombre de la finca requerido para la importación del REA.');
		}// if();

		for (const detail of errorDetails) {
			this.messageService.add({
				severity: 'error',
				summary: '',
				detail,
			});
		}// for();

		return errorDetails.length === 0;
	} // ();

	/***************************************************************************** */
} // class;
